import { createTheme } from '@mui/material/styles';

// Темная тема для ПОМОГАЙ.ONLINE
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#818CF8', // Lighter Indigo для темной темы
      light: '#A5B4FC',
      dark: '#6366F1',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FBBF24', // Lighter Amber для темной темы
      light: '#FCD34D',
      dark: '#F59E0B',
      contrastText: '#000000',
    },
    success: {
      main: '#34D399', // Lighter Emerald
      light: '#6EE7B7',
      dark: '#10B981',
      contrastText: '#000000',
    },
    error: {
      main: '#F87171', // Lighter Red
      light: '#FCA5A5',
      dark: '#EF4444',
      contrastText: '#000000',
    },
    warning: {
      main: '#FBBF24',
      light: '#FCD34D',
      dark: '#F59E0B',
      contrastText: '#000000',
    },
    info: {
      main: '#60A5FA', // Lighter Blue
      light: '#93C5FD',
      dark: '#3B82F6',
      contrastText: '#000000',
    },
    background: {
      default: '#0F172A', // Slate-900
      paper: '#1E293B', // Slate-800
    },
    text: {
      primary: '#F1F5F9', // Slate-100
      secondary: '#CBD5E1', // Slate-300
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    gradient: {
      primary: 'linear-gradient(135deg, #818CF8 0%, #A78BFA 100%)',
      secondary: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
      success: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
      error: 'linear-gradient(135deg, #F87171 0%, #EF4444 100%)',
      info: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      '@media (max-width:600px)': {
        fontSize: '1.125rem',
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      '@media (max-width:600px)': {
        fontSize: '0.875rem',
      },
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(0,0,0,0.3)',
    '0 4px 12px rgba(0,0,0,0.35)',
    '0 6px 16px rgba(0,0,0,0.4)',
    '0 8px 24px rgba(0,0,0,0.45)',
    '0 12px 32px rgba(0,0,0,0.5)',
    '0 16px 40px rgba(0,0,0,0.55)',
    '0 20px 48px rgba(0,0,0,0.6)',
    '0 24px 56px rgba(0,0,0,0.65)',
    '0 8px 32px rgba(129, 140, 248, 0.3)',
    ...Array(15).fill('0 4px 24px rgba(0,0,0,0.4)'),
  ] as any,
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          background: 'rgba(30, 41, 59, 0.8)', // Slate-800 with transparency
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.6)',
            border: '1px solid rgba(129, 140, 248, 0.3)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
          textTransform: 'none',
          padding: '10px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(129, 140, 248, 0.4)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(129, 140, 248, 0.5)',
            transform: 'translateY(-2px)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #818CF8 0%, #A78BFA 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
          },
        },
        outlined: {
          textTransform: 'none',
          borderWidth: 2,
          borderColor: 'rgba(129, 140, 248, 0.5)',
          '&:hover': {
            borderWidth: 2,
            borderColor: 'rgba(129, 140, 248, 0.8)',
            transform: 'translateY(-2px)',
            background: 'rgba(129, 140, 248, 0.1)',
          },
        },
        text: {
          textTransform: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minWidth: 72,
          color: '#CBD5E1',
          '&.Mui-selected': {
            color: '#818CF8',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 10,
          padding: '4px 8px',
        },
        filled: {
          background: 'linear-gradient(135deg, #818CF8 0%, #A78BFA 100%)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
        elevation1: {
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
        },
        elevation2: {
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        },
        elevation3: {
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(129, 140, 248, 0.5)',
              boxShadow: '0 4px 12px rgba(129, 140, 248, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#818CF8',
              boxShadow: '0 4px 16px rgba(129, 140, 248, 0.3)',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          background: 'rgba(30, 41, 59, 0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
});

export default darkTheme;
